import React, { useContext, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { ReactComponent as LogoImage } from '../../images/logo-full.svg';
import accountImage from '../../images/login-oil.png';
import { login, LoginRequest, getMe } from '../../services/api';
import { AuthContext } from '../../components/auth/AuthContext';
import { LoadingContext } from '../../contexts/LoadingContext';
import { showSuccessToastFullScreen, showErrorToastFullScreen } from '../../utils/toastUtils';
import {
  handleError,
  getApiErrorCode,
  isTurnstileTokenSpent,
  ApiErrorCode,
} from '../../utils/errorUtils';
import { isSafeRedirect } from '../../utils/redirectUtils';
import { isPrivilegedRole, startSession } from '../../utils/authUtils';
import { identifyUser, captureEvent } from '../../services/analytics';
import { setMonitoringUser } from '../../services/monitoring';
import AuthFooter from '../../components/auth/AuthFooter';
import TurnstileWidget, {
  TurnstileWidgetHandle,
  TURNSTILE_MESSAGE_ID,
} from '../../components/auth/TurnstileWidget';


import styles from './SignIn.module.scss';

const TURNSTILE_ERROR_ID = 'sign-in-turnstile-error';

// Shown when the user submits with no token. The button stays enabled on
// purpose — a content blocker leaves the token permanently empty, and a
// permanently disabled button explains nothing — so this message is the whole
// explanation the user gets for the click that did nothing.
const TURNSTILE_REQUIRED_MESSAGE =
  'Please complete the verification check above, then try again.';

// Shown when the backend rejected the token (403 turnstile_verification_failed).
// Deliberately does not promise a challenge to solve: in managed mode the reset
// usually re-issues a token silently and there is nothing to do but resubmit. It
// also has to stay compatible with TURNSTILE_REQUIRED_MESSAGE, which is what a
// user sees if they resubmit before the new token lands — one retry, one
// coherent instruction.
const TURNSTILE_REJECTED_MESSAGE =
  "We couldn't confirm that verification. The check above has been refreshed — please complete it if prompted, then try again.";


const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAccessToken, setUserId } = useContext(AuthContext);
  const { setIsLoading } = useContext(LoadingContext);
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileError, setTurnstileError] = useState<string>();
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Perform form validation
    const form = event.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    // Refuse to POST without a token. The backend would answer 403, we would
    // reset the widget, and an unsolvable widget (blocked script) would put the
    // user in a 403 -> reset -> 403 loop; stopping here makes that impossible.
    const turnstileToken = turnstileRef.current?.getToken() ?? '';
    if (!turnstileToken) {
      setTurnstileError(TURNSTILE_REQUIRED_MESSAGE);
      return;
    }
    setTurnstileError(undefined);

    setIsLoading(true);

    try {
      const formData = new FormData(form);
      const userData: LoginRequest = {
        username: formData.get('email') as string,
        password: formData.get('password') as string,
      };

      const loginResponse = await login(userData, turnstileToken);
      // Store the new App Token in localStorage
      setAccessToken(loginResponse.accessToken);
      startSession(loginResponse.accessToken, loginResponse.expiration);

      // Get and store the user details
      const getMeResponse = await getMe();
      localStorage.setItem('userFirstName', getMeResponse.firstName);
      setUserId(getMeResponse.id);
      localStorage.setItem('userId', getMeResponse.id);
      localStorage.setItem('userRole', getMeResponse.role);
      if(isPrivilegedRole(getMeResponse.role))
      {
        localStorage.setItem('adminAccessToken', loginResponse.accessToken);
        localStorage.setItem('adminAccessTokenExpiration', loginResponse.expiration);
        // The admin's OWN identity, kept beside their own token. Impersonation
        // overwrites `userId`/`userFirstName` with the target's, so this is the
        // only surviving record of who is really driving -- and
        // `stopImpersonating` has nothing to restore without it. Written here
        // rather than at impersonation time because here is where the values
        // are known to be the admin's: by the time the console offers the
        // button, `getMe` has long since been the target's.
        localStorage.setItem('adminUserId', getMeResponse.id);
        localStorage.setItem('adminUserFirstName', getMeResponse.firstName);
      }

      identifyUser(getMeResponse.id, {
        role: getMeResponse.role,
        email_domain: userData.username.split('@')[1],
      });
      setMonitoringUser({ id: getMeResponse.id, role: getMeResponse.role });
      captureEvent('user_signed_in', { role: getMeResponse.role });

      showSuccessToastFullScreen('Signed in successfully.', 500);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const params = new URLSearchParams(location.search);
      const requestedRedirect = params.get('redirect');
      const target = isSafeRedirect(requestedRedirect) ? requestedRedirect : '/';
      navigate(target, { replace: true });

    } catch (error) {
      // Two separate questions: is the token spent (reset), and what do we tell
      // the user (below). /oauth/token verifies BEFORE it checks the password,
      // so a 401 has already redeemed the token — not resetting here is what
      // would make the corrected password fail too and blame verification for a
      // typo. Never auto-resubmit after a reset: managed mode may escalate a
      // previously silent user to an interactive challenge, so the user acts.
      if (isTurnstileTokenSpent(error)) {
        turnstileRef.current?.reset();
      }

      if (getApiErrorCode(error) === ApiErrorCode.TurnstileVerificationFailed) {
        setTurnstileError(TURNSTILE_REJECTED_MESSAGE);
      } else if (error instanceof Error && (error as any).response && (error as any).response.status === 401) {
          showErrorToastFullScreen('Incorrect username or password.');
      } else {
        handleError(error);
      }
    }

    finally
    {
      setIsLoading(false);
    }
  };

  const handleCreateAccount = () => {
    // Navigate to the create account page
    navigate('/registration/create-account');
  };

  return (
    <div className={styles.container}>
      <div className={styles.logo}>
        <a href="https://jobbeast.org" target="_blank" rel="noopener noreferrer">
          <LogoImage className={styles.logo} />
        </a>
      </div>
      <div className={styles.formContainer}>
        <h1>Sign in to your account</h1>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="email">Email *</label>
              <input type="email" id="email" name="email" required />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="password">Password *</label>
              <div className={styles.passwordWrap}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  required
                />
                <button
                  type="button"
                  className={styles.revealToggle}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeInvisibleOutlined aria-hidden /> : <EyeOutlined aria-hidden />}
                </button>
              </div>
              <Link to="/forgot-password" className={styles.forgotLink}>Forgot password?</Link>
            </div>
          </div>
          <TurnstileWidget
            ref={turnstileRef}
            className={styles.turnstile}
            action="login"
            // Non-blocking, and narrow: a fresh token answers the "you have
            // not solved it yet" prompt and nothing else. Clearing the
            // rejection message too would wipe it the moment managed mode
            // silently re-issues a token, leaving no instruction to resubmit.
            // It deliberately does NOT gate the button.
            onTokenChange={(token) => {
              if (!token) return;
              setTurnstileError((current) =>
                current === TURNSTILE_REQUIRED_MESSAGE ? undefined : current,
              );
            }}
          />
          {turnstileError && (
            <p id={TURNSTILE_ERROR_ID} role="alert" className={styles.turnstileError}>
              {turnstileError}
            </p>
          )}
          <div className={styles.buttonContainer}>
            <button
              type="submit"
              // TURNSTILE_MESSAGE_ID exists only once the widget has something
              // to say (script blocked / still loading), and a dangling
              // reference is ignored until then. It has to be here regardless:
              // that message is the only explanation for a submit that cannot
              // work, and it gates signing in.
              aria-describedby={[TURNSTILE_MESSAGE_ID, turnstileError ? TURNSTILE_ERROR_ID : '']
                .filter(Boolean)
                .join(' ')}
            >
              Sign In
            </button>
            <button className={styles.newAccountButton} onClick={handleCreateAccount}>
              Create New Account
            </button>
          </div>
        </form>
      </div>
      <div className={styles.imageContainer}>
        <img src={accountImage} alt="The Beast applying for jobs" />
        <AuthFooter />
      </div>
    </div>
  );
};

export default SignIn;